import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Loader2, X } from 'lucide-react';

interface SyncProgressModalProps {
  isOpen: boolean;
  onClose: () => void;
  syncStats?: {
    added: number;
    updated: number;
    duplicatesRemoved: number;
  };
  isLoading: boolean;
}

export default function SyncProgressModal({ isOpen, onClose, syncStats, isLoading }: SyncProgressModalProps) {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => !isLoading && onClose()}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex justify-between items-center mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    {isLoading ? 'Sincronizando Ordens...' : 'Sincronização Concluída'}
                  </Dialog.Title>
                  {!isLoading && (
                    <button
                      type="button"
                      className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                      onClick={onClose}
                    >
                      <X className="h-6 w-6" />
                    </button>
                  )}
                </div>

                {isLoading ? (
                  <div className="mt-4 flex flex-col items-center">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <p className="mt-2 text-sm text-gray-500">
                      Por favor, aguarde enquanto sincronizamos suas ordens...
                    </p>
                    <button
                      type="button"
                      className="mt-4 inline-flex justify-center rounded-md border border-transparent bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2"
                      onClick={onClose}
                    >
                      Cancelar
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 mb-2">
                        Novas ordens adicionadas: <span className="font-medium text-gray-900">{syncStats?.added}</span>
                      </p>
                      <p className="text-sm text-gray-500 mb-2">
                        Ordens atualizadas: <span className="font-medium text-gray-900">{syncStats?.updated}</span>
                      </p>
                      {syncStats?.duplicatesRemoved > 0 && (
                        <p className="text-sm text-gray-500">
                          Duplicatas removidas: <span className="font-medium text-gray-900">{syncStats?.duplicatesRemoved}</span>
                        </p>
                      )}
                    </div>

                    <div className="mt-4">
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                        onClick={onClose}
                      >
                        Fechar
                      </button>
                    </div>
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
